// src/pages/Cloud/MultiCloudComplianceDashboard.js
import React, { useEffect } from 'react';
import { Spinner } from 'react-bootstrap';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ComplianceRateCircle from './charts/ComplianceRateCircle';
import '../../style/scss/MultiCloudComplianceDashboard.scss';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCloudDashboardData } from '../../store/slices/cloudDashboardSlice';

function CloudBarChart({ title, data }) {
  return (
    <div className="chart-row">
      <div className="chart-container">
        <h3 className="chart-title">{title}</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="month" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #e0e0e0',
                borderRadius: '4px',
              }}
            />
            <Legend wrapperStyle={{ fontSize: '12px' }} iconType="circle" />
            <Bar dataKey="AWS" stackId="a" fill="#3F52EC" radius={[0, 0, 0, 0]} barSize={30} />
            <Bar dataKey="GCP" stackId="a" fill="#56D9FE" radius={[0, 0, 0, 0]} barSize={30} />
            <Bar dataKey="Azure" stackId="a" fill="#9470F0" radius={[4, 4, 0, 0]} barSize={30} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function MultiCloudComplianceDashboard() {
  const dispatch = useDispatch();

  const { cloudPassedData, cloudHighRiskData, cloudMediumRiskData, cloudLowRiskData, riskAssessment, loading, error } =
    useSelector((state) => state.cloudDashboard);

  useEffect(() => {
    dispatch(fetchCloudDashboardData());
  }, [dispatch]);

  // 合規統計數據（來自後端 risk_assessment.data[0]）
  const riskRow = riskAssessment?.data?.[0] || null;

  const complianceStats = {
    passed: riskRow?.PassedAssessments ?? 0,
    total: riskRow?.TotalAssessments ?? (riskRow?.PassedAssessments ?? 0) + (riskRow?.FailedAssessments ?? 0),
    percentage: Number.isFinite(Number(riskRow?.CompliancePercentage))
      ? Math.round(Number(riskRow.CompliancePercentage))
      : 0,
  };

  // 回傳空陣列避免 Recharts 出錯
  const safeCloudPassedData = Array.isArray(cloudPassedData) ? cloudPassedData : [];
  const safeCloudHighRiskData = Array.isArray(cloudHighRiskData) ? cloudHighRiskData : [];
  const safeCloudMediumRiskData = Array.isArray(cloudMediumRiskData) ? cloudMediumRiskData : [];
  const safeCloudLowRiskData = Array.isArray(cloudLowRiskData) ? cloudLowRiskData : [];

  const errorMessage =
    typeof error === 'string'
      ? error
      : error?.error || error?.detail || error?.message || (error ? JSON.stringify(error) : '');

  if (loading && !riskRow && safeCloudPassedData.length === 0) {
    return (
      <div className="multi-cloud-dashboard">
        <div className="dashboard-status">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">載入中</span>
          </Spinner>
          <p>正在載入多雲合規資料</p>
        </div>
      </div>
    );
  }

  return (
    <div className="multi-cloud-dashboard">
      {/* 頂部橫幅區域 */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">多雲合規性檢視</h1>
          <div className="compliance-stats">
            <div className="stat-card compliance-numbers">
              <div className="stat-label">合規數</div>
              <div className="stat-numbers">
                <div className="number-item">
                  <span className="value">{loading ? '—' : complianceStats.passed}</span>
                </div>
                <div className="number-item">
                  <span className="value">{loading ? '—' : complianceStats.total}</span>
                </div>
              </div>
              <div className="stat-labels">
                <div className="label-item">
                  <span className="icon purple"></span>
                  <span className="text">符合合規數</span>
                </div>
                <div className="label-item">
                  <span className="icon orange"></span>
                  <span className="text">總合規數</span>
                </div>
              </div>
            </div>

            <div className="stat-card compliance-rate">
              <div className="stat-label">合規通過率</div>
              <div className="circle-container">
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <ComplianceRateCircle percentage={complianceStats.percentage} size={140} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {errorMessage ? (
        <div className="dashboard-error" role="alert">
          無法載入多雲合規資料：{errorMessage}
        </div>
      ) : null}

      {/* 圖表區域 */}
      <div className="dashboard-charts">
        <CloudBarChart title="三大公有雲通過數量" data={safeCloudPassedData} />
        <CloudBarChart title="三大公有雲高風險數量" data={safeCloudHighRiskData} />
        <CloudBarChart title="三大公有雲中風險數量" data={safeCloudMediumRiskData} />
        <CloudBarChart title="三大公有雲低風險數量" data={safeCloudLowRiskData} />
      </div>
    </div>
  );
}
